<script setup lang="ts">
import { computed } from 'vue'
import { latestMintService } from '~/api/service/latest-mint.service'
import AddressLink from '~/components/link/AddressLink.vue'
import BcLink from '~/ui/Link/BcLink.vue'
import LoadingDashboardList from '~/components/loading/LoadingDashboardList.vue'
import Skeleton from '~/components/loading/Skeleton.vue'
import { INftType } from '~/types/token-type.type'
import MintCardAnimatedWrapper from './MintCardAnimatedWrapper.vue'

const { data, isLoading } = latestMintService.useGetLatestMint()

const currentItem = computed(() => {
  return data.value && data.value.length > 0 ? data.value[0] : null
})

const getImageUrl = (item: NonNullable<typeof data.value>[0]) => {
  return item.IMGURL || ''
}

const abbreviateAddress = (address: string) => {
  if (!address) return ''
  if (address.length <= 10) return address
  return `${address.slice(0, 6)}...${address.slice(-4)}`
}
</script>

<template>
  <div class="latest-mint-area flex-col">
    <MintCardAnimatedWrapper>
      <template v-if="isLoading || !data || !currentItem">
        <div class="flex items-center gap-x-2">
          <Skeleton class="skeleton-address" height="12px" />
          <span class="minted-text">minted</span>
          <Skeleton class="skeleton-quantity" height="12px" />
        </div>
        <div class="flex items-center gap-x-2">
          <div class="nft-icon-container">
            <Skeleton class="skeleton-icon" height="24px" />
          </div>
          <Skeleton class="skeleton-name" height="12px" />
          <Skeleton class="skeleton-timestamp" height="12px" />
        </div>
      </template>
      <template v-else>
        <div class="flex items-center gap-x-2">
          <AddressLink
            :address="currentItem.ADDRSTOINFO.ADDR"
            :type="currentItem.ADDRSTOINFO.ADT"
            :name="abbreviateAddress(currentItem.ADDRSTOINFO.ADDR)"
            :tooltip="currentItem.ADDRSTOINFO.ADDR"
            class="address-link"
          />
          <span class="minted-text">minted</span>
          <span class="quantity-text">{{ currentItem.VAL }}</span>
        </div>
        <div class="flex items-center gap-x-2">
          <div class="nft-icon-container">
            <img
              v-if="getImageUrl(currentItem)"
              :src="getImageUrl(currentItem)"
              :alt="`${currentItem.TKNNM} image`"
              class="nft-icon"
            >
            <FontAwesomeIcon
              v-else
              icon="gift"
              class="nft-icon-fallback"
            />
          </div>
          <BcLink
            :to="routes.getNftItemDetailPageWithTab(currentItem.TP as INftType, currentItem.TKNCTR.ADDR, currentItem.TKNID)"
            :content="currentItem.TKNNM"
            :tooltip="currentItem.TRXHA"
            class="nft-name-text"
          />
          <span class="timestamp-text">({{ currentItem.AG }})</span>
        </div>
      </template>
    </MintCardAnimatedWrapper>
  </div>
</template>

<style scoped lang="scss">
.latest-mint-area {
  margin: 0;
}

@media (min-width: 841px) and (max-width: 1280px) {
  .latest-mint-area {
    margin-top: -1.5rem;
    margin-bottom: -1rem;
  }
}

@media (min-width: 1281px) {
  .latest-mint-area {
    margin-top: -2rem;
    margin-bottom: -2rem;
  }
}

:deep(.mint-card-content) {
  .address-link {
    font-size: 12px;
    :deep(.text-explorer-blue) {
      color: #4A90E2;
    }

    :deep(a) {
      color: #4A90E2;
    }
  }

  .minted-text {
    font-size: 12px;
    color: #E0E0E0;
  }

  .quantity-text {
    @apply font-bold;
    font-size: 12px;
    color: #FFFFFF;
  }

  .nft-icon-container {
    @apply flex items-center justify-center shrink-0 p-1;
    width: 24px;
    height: 24px;

    @include sm {
      @apply p-1;
      width: 24px;
      height: 24px;
    }

    .nft-icon {
      @apply w-full h-full object-cover rounded;
    }

    .nft-icon-fallback {
      @apply text-primary;
      font-size: 16px;

      @include sm {
        font-size: 14px;
      }
    }
  }

  .nft-name-text {
    @apply font-medium no-underline;
    font-size: 12px;
    color: #4A90E2;

    &:hover {
      color: #4A90E2;
      text-decoration: none;
    }
  }

  .timestamp-text {
    font-size: 12px;
    color: #A0A0A0;
  }

  .skeleton-address {
    width: 90px;

    @include sm {
      width: 80px;
    }
  }

  .skeleton-quantity {
    width: 20px;
  }

  .skeleton-icon {
    width: 24px;
    border-radius: 4px;
  }

  .skeleton-name {
    width: 180px;

    @include sm {
      width: 150px;
    }
  }

  .skeleton-timestamp {
    width: 80px;

    @include sm {
      width: 70px;
    }
  }
}
</style>
